import React, { useState, useEffect } from 'react';
import { DocumentFolder, db } from '../../lib/db';
import { Folder, FolderPlus, Edit2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

interface FolderManagerProps {
  matterId?: string | null;
  projectId?: string | null;
  dbUserId: string;
  onClose: () => void;
}

function FolderManager({ matterId, projectId, dbUserId, onClose }: FolderManagerProps) {
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState<DocumentFolder | null>(null);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    try {
      let folderList: DocumentFolder[] = [];
      
      if (matterId) {
        folderList = await db.documentFolders.findByMatterId(matterId, dbUserId);
      } else if (projectId) {
        folderList = await db.documentFolders.findByProjectId(projectId, dbUserId);
      }
      
      setFolders(folderList);
    } catch (error) {
      console.error('Error loading folders:', error);
      toast.error('Failed to load folders');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    
    try {
      const maxPosition = Math.max(...folders.map(f => f.position), -1);
      
      await db.documentFolders.create({
        user_id: dbUserId,
        name: newFolderName.trim(),
        matter_id: matterId,
        project_id: projectId,
        position: maxPosition + 1
      });
      
      setNewFolderName('');
      await loadFolders();
      toast.success('Folder created successfully');
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !editedName.trim()) return;
    
    try {
      await db.documentFolders.update(editingFolder.id, dbUserId, {
        name: editedName.trim()
      });
      
      setEditingFolder(null);
      setEditedName('');
      await loadFolders();
      toast.success('Folder updated successfully');
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folder: DocumentFolder) => {
    if (!confirm(`Are you sure you want to delete "${folder.name}"? Documents in this folder will not be deleted.`)) {
      return;
    }
    
    try {
      await db.documentFolders.delete(folder.id, dbUserId);
      await loadFolders();
      toast.success('Folder deleted successfully');
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  const buildFolderTree = (folders: DocumentFolder[], parentId: string | null = null): DocumentFolder[] => {
    return folders
      .filter(f => f.parent_folder_id === parentId)
      .sort((a, b) => a.position - b.position);
  };

  const renderFolderTree = (parentId: string | null = null, level = 0): JSX.Element[] => {
    const foldersAtLevel = buildFolderTree(folders, parentId);
    
    return foldersAtLevel.map(folder => (
      <div key={folder.id}>
        <div
          className={`flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded ${
            level > 0 ? `ml-${level * 4}` : ''
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
        >
          <div className="flex items-center space-x-2 flex-1">
            <Folder className="w-4 h-4 text-gray-400" />
            {editingFolder?.id === folder.id ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUpdateFolder()}
                onBlur={handleUpdateFolder}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                autoFocus
              />
            ) : (
              <span className="text-sm text-gray-700">{folder.name}</span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => {
                setEditingFolder(folder);
                setEditedName(folder.name);
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Edit folder"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteFolder(folder)}
              className="p-1 text-gray-400 hover:text-red-600"
              title="Delete folder"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        {renderFolderTree(folder.id, level + 1)}
      </div>
    ));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Manage Folders</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Folder List */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {folders.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No folders yet</p>
          ) : (
            <div className="space-y-1">
              {renderFolderTree()}
            </div>
          )}
        </div>
        
        {/* New Folder Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              placeholder="New folder name"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleCreateFolder}
              className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
            >
              <FolderPlus className="w-4 h-4 mr-1" />
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FolderManager;